/* begin general import */
import { getInitials } from "helpers/initials";
import React from "react";
import { Dropdown, Input } from "antd";
import FormItem from "components/Utility/FormItem/FormItem";
import { formService } from "services/form-service";
/* end general import */

/* begin individual import */
import { APP_USER_MASTER_ROUTE } from "config/route-consts";
import { getActivityLog, parseTemplate } from "helpers/activity-log-helper";
import { formatDate } from "helpers/date-time";
import { Link } from "react-router-dom";
import { AppUser } from "models/AppUser";
import { appUserRepository } from "repositories/app-user-repository";
import { useAppUserDetailHook } from "views/AppUserView/AppUserDetail/AppUserDetailHook";
import {
  useLockAccountModal,
  LockAccountModal,
} from "views/AppUserView/AppUserDetail/LockAccountModal";
import {
  useForgotPasswordModal,
  ForgotPasswordModal,
} from "views/AppUserView/AppUserDetail/ForgotPasswordModal";
import {
  useActivityModal,
  ActivityModal,
} from "views/AppUserView/AppUserDetail/ActivityModal";
import AppUserDetailRoleModal from "./AppUserDetailRoleModal/AppUserDetailRoleModal";
import { useAppUserDetailRoleModal } from "./AppUserDetailRoleModal/AppUserDetailRoleModalHook";
import "./AppUserDetail.scss";
import authenticationService from "services/authentication-service";
import { API_APP_USER_PREFIX } from "config/api-consts";
/* end individual import */

const RECENT_ACTIVITY_LIMIT = 6;


// Chi tiết tài khoản admin: cùng khuôn với các trang chi tiết khác (đầu trang + lưới 2fr/1fr).
function AppUserDetail() {
  const { validAction } = authenticationService.useAction(
    "appUser",
    API_APP_USER_PREFIX
  );

  const {
    model,
    isOpenRoleModal,
    handleOpenRoleModal,
    handleCloseRoleModal,
    handleSaveModel,
    handleChangeSimpleField,
    handleUpdateNewModel,
    loading,
  } = useAppUserDetailRoleModal(appUserRepository.get, appUserRepository.save);

  const {
    isEditable,
    handleEdit,
    handleFinish,
    handleActiveAccount,
    handleDeactiveAccount,
  } = useAppUserDetailHook(model, handleUpdateNewModel);

  const handleSaveDetail = React.useCallback(() => {
    handleFinish();
    handleSaveModel();
  }, [handleFinish, handleSaveModel]);

  const handleCancelDetail = React.useCallback(() => {
    handleFinish();
  }, [handleFinish]);

  const {
    visibleLockAccountModal,
    openLockAccountModal,
    cancelLockAccountModal,
    activeAccountModal,
    deactiveAccountmodal,
  } = useLockAccountModal(handleDeactiveAccount, handleActiveAccount);

  const {
    visibleForgotPasswordModal,
    openForgotPasswordModal,
    cancelForgotPasswordModal,
    saveForgotPasswordModal,
  } = useForgotPasswordModal();

  const {
    loading: activityLoading,
    list,
    visibleActivityModal,
    openActivityModal,
    cancelActivityModal,
    handleApplyFilter,
  } = useActivityModal(appUserRepository.getActivityLog);

  const isActive = model.statusId === 1;
  const roles = model.appUserRoleMappings ?? [];
  // Vài hoạt động mới nhất; xem đủ thì mở modal "Xem toàn bộ".
  const recentActivities = React.useMemo(
    () =>
      (model.appUserActivityLogs ?? []).slice(0, RECENT_ACTIVITY_LIMIT).map((item) => {
        const log = getActivityLog(item.action?.message, item.action?.params ?? {});
        const text = parseTemplate(log?.message, log?.params ?? {}).trim();
        return { id: item.id, time: item.createdAt, text: text.charAt(0).toUpperCase() + text.slice(1) };
      }),
    [model.appUserActivityLogs]
  );

  // Một dòng nhãn + giá trị; lúc sửa thì giá trị thành ô nhập.
  const field = (label: string, key: keyof AppUser, placeholder: string) => (
    <div className="app-user-detail__row">
      <dt>{label}</dt>
      <dd>
        {isEditable ? (
          <FormItem
            validateStatus={formService.getValidationStatus<AppUser>(model.errors, key as string)}
            message={model.errors?.[key as string]}
          >
            <Input
              value={(model[key] as string) ?? ""}
              placeholder={placeholder}
              onChange={(event) => handleChangeSimpleField(key as string)(event.target.value)}
            />
          </FormItem>
        ) : (
          (model[key] as string) || "—"
        )}
      </dd>
    </div>
  );

  const moreMenu = (
    <div className="app-user-detail__menu">
      <button
        type="button"
        className={isActive ? "app-user-detail__menu-item--danger" : undefined}
        onClick={openLockAccountModal}
      >
        <i className={isActive ? "tio-lock_outlined" : "tio-lock_open_outlined"} />
        {isActive ? "Khoá tài khoản" : "Mở khoá tài khoản"}
      </button>
    </div>
  );

  return (
    <>
      <div className="app-user-detail">
        {/* ----- Đầu trang ----- */}
        <div className="app-user-detail__head">
          <div className="app-user-detail__identity">
            {model.avatar ? (
              <img className="app-user-detail__avatar" src={model.avatar} alt="" />
            ) : (
              <span className="app-user-detail__avatar">
                {getInitials(model.displayName || model.username)}
              </span>
            )}
            <div className="app-user-detail__head-text">
              <Link to={APP_USER_MASTER_ROUTE} className="page-back">
                <i className="tio-chevron_left" aria-hidden />
                Tài khoản admin
              </Link>
              <h1 className="app-user-detail__title">
                {model.displayName || model.username || "—"}
                {model.status && (
                  <span
                    className={`status-badge status-badge--${isActive ? "success" : "error"}`}
                  >
                    {model.status.name}
                  </span>
                )}
              </h1>
              <p className="app-user-detail__meta">
                {[model.username, model.email].filter(Boolean).join(" · ") || "—"}
              </p>
            </div>
          </div>

          <div className="app-user-detail__actions">
            {isEditable ? (
              <>
                <button type="button" className="app-user-detail__btn" onClick={handleCancelDetail}>
                  Huỷ
                </button>
                <button
                  type="button"
                  className="app-user-detail__btn app-user-detail__btn--primary"
                  onClick={handleSaveDetail}
                >
                  Lưu thay đổi
                </button>
              </>
            ) : (
              <>
                {validAction("update") && (
                  <button type="button" className="app-user-detail__btn" onClick={openForgotPasswordModal}>
                    <i className="tio-password_open" />
                    Đổi mật khẩu
                  </button>
                )}
                {validAction("update") && (
                  <button
                    type="button"
                    className="app-user-detail__btn app-user-detail__btn--primary"
                    onClick={handleEdit}
                  >
                    <i className="tio-edit" />
                    Sửa thông tin
                  </button>
                )}
                {validAction("lock") && (
                  <Dropdown overlay={moreMenu} trigger={["click"]} placement="bottomRight">
                    <button
                      type="button"
                      className="app-user-detail__btn app-user-detail__btn--icon"
                      aria-label="Thao tác khác"
                    >
                      <i className="tio-more_horizontal" />
                    </button>
                  </Dropdown>
                )}
              </>
            )}
          </div>
        </div>

        <div className="app-user-detail__grid">
          {/* ----- Cột chính ----- */}
          <div className="app-user-detail__stack">
            <section className="app-user-detail__card">
              <div className="app-user-detail__card-head">
                <h2 className="app-user-detail__card-title">Thông tin tài khoản</h2>
                {isEditable && <span className="app-user-detail__editing">Đang sửa</span>}
              </div>
              <dl className="app-user-detail__rows">
                {field("Tên đăng nhập", "username", "Nhập tên đăng nhập")}
                {field("Họ và tên", "displayName", "Nhập họ và tên")}
                {field("Email", "email", "Nhập email")}
                {field("Vị trí", "position", "Nhập vị trí")}
                <div className="app-user-detail__row">
                  <dt>Tình trạng</dt>
                  <dd>
                    {model.status ? (
                      <span
                        className={`status-badge status-badge--${isActive ? "success" : "error"}`}
                      >
                        {model.status.name}
                      </span>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="app-user-detail__card">
              <div className="app-user-detail__card-head">
                <h2 className="app-user-detail__card-title">Hoạt động gần đây</h2>
                {recentActivities.length > 0 && (
                  <button type="button" className="app-user-detail__more" onClick={openActivityModal}>
                    Xem toàn bộ
                    <i className="tio-chevron_right" />
                  </button>
                )}
              </div>
              {recentActivities.length === 0 ? (
                <p className="app-user-detail__empty">Chưa có hoạt động nào</p>
              ) : (
                <ul className="app-user-detail__feed">
                  {recentActivities.map((activity, index) => (
                    <li key={activity.id ?? index}>
                      <span className="app-user-detail__feed-time tabular-nums">
                        {formatDate(activity.time, "DD/MM HH:mm")}
                      </span>
                      <span className="app-user-detail__feed-text">{activity.text || "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* ----- Cột phụ ----- */}
          <div className="app-user-detail__stack">
            <section className="app-user-detail__card">
              <div className="app-user-detail__card-head">
                <h2 className="app-user-detail__card-title">
                  Vai trò <span className="app-user-detail__count">· {roles.length}</span>
                </h2>
                {validAction("update") && (
                  <button type="button" className="app-user-detail__more" onClick={() => handleOpenRoleModal()}>
                    Sửa vai trò
                  </button>
                )}
              </div>
              {roles.length === 0 ? (
                <p className="app-user-detail__empty">Tài khoản chưa có vai trò nào</p>
              ) : (
                <ul className="app-user-detail__roles">
                  {roles.map((mapping) => (
                    <li key={mapping.role?.id}>
                      <span className="app-user-detail__role-icon">
                        <i className="tio-security_on_outlined" />
                      </span>
                      <span className="app-user-detail__role-name">{mapping.role?.name || "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>
      <LockAccountModal
        statusId={model.statusId}
        visibleLockAccountModal={visibleLockAccountModal}
        cancelLockAccountModal={cancelLockAccountModal}
        activeAccountModal={activeAccountModal}
        deactiveAccountmodal={deactiveAccountmodal}
      />
      <ForgotPasswordModal
        model={model}
        visibleForgotPasswordModal={visibleForgotPasswordModal}
        cancelForgotPasswordModal={cancelForgotPasswordModal}
        saveForgotPasswordModal={saveForgotPasswordModal}
      />
      <ActivityModal
        model={list}
        loading={activityLoading}
        visibleActivityModal={visibleActivityModal}
        cancelActivityModal={cancelActivityModal}
        onFilter={handleApplyFilter}
      />
      <AppUserDetailRoleModal
        model={model}
        visible={isOpenRoleModal}
        getRoles={appUserRepository.singleListRole}
        handleSave={handleSaveModel}
        handleCancel={handleCloseRoleModal}
        loading={loading}
        visibleFooter={false}
      />
    </>
  );
}

export default AppUserDetail;
